import React from 'react';
import { defaultProps, mapProps } from '@team-griffin/rehook';
import pureEnhance from '@ux/pure-enhance';
import { DynamicLink } from '@ux/dynamic-link';
import { percentage, modifier, withStylesHook } from '@ux/style-utils';
import reactCSS, { hover, handleActive } from 'reactcss';
import cssSides, { Side } from '@team-griffin/css-sides';
import longhand from '@team-griffin/css-longhand';
import px2em from '@ux/px2em';
import { content } from '@ux/type-size';
import { cond, equals, always, T, omit } from 'ramda';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
}

var Size;

(function (Size) {
  Size["SM"] = "SM";
  Size["MD"] = "MD";
  Size["LG"] = "LG";
  Size["INHERIT"] = "INHERIT";
})(Size || (Size = {}));

var Against;

(function (Against) {
  Against["LIGHT"] = "LIGHT";
  Against["NEUTRAL"] = "NEUTRAL";
  Against["DARK"] = "DARK";
  Against["PRIMARY"] = "PRIMARY";
  Against["PRIMARY_FADED"] = "PRIMARY_FADED";
  Against["HIGHLIGHT"] = "HIGHLIGHT";
  Against["HIGHLIGHT_FADED"] = "HIGHLIGHT_FADED";
})(Against || (Against = {}));

var ContentType;

(function (ContentType) {
  ContentType["DEFAULT"] = "DEFAULT";
  ContentType["PHONE_NUMBER"] = "PHONE_NUMBER";
})(ContentType || (ContentType = {}));

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var stylesheet = function stylesheet(_ref) {
  var _reactCSS;

  var theme = _ref.theme,
      against = _ref.against,
      block = _ref.block,
      disabled = _ref.disabled,
      size = _ref.size,
      caps = _ref.caps,
      contentType = _ref.contentType,
      hover = _ref.hover,
      active = _ref.active;
  var palette = theme.palette,
      typography = theme.typography;
  var fill = cond([[equals(Against.LIGHT), always(palette.secondary)], [equals(Against.NEUTRAL), always(palette.secondary)], [equals(Against.DARK), always(palette.tertiary)], [equals(Against.PRIMARY), always(palette.primaryContrast)], [equals(Against.PRIMARY_FADED), always(palette.secondary)], [equals(Against.HIGHLIGHT), always(palette.highlightContrast)], [equals(Against.HIGHLIGHT_FADED), always(palette.highlight)], [T, always(palette.secondary)]])(against);
  return reactCSS((_reactCSS = {
    default: {
      root: Object.assign({}, content(theme.typography), longhand('border', {
        color: 'transparent',
        style: 'solid',
        width: 0
      }), cssSides('margin', Side.A, 0), cssSides('padding', Side.A, 0), {
        fontFamily: typography.content,
        lineHeight: 1.1,
        paddingBottom: 1,
        color: fill,
        textAlign: 'left',
        backgroundColor: 'transparent',
        overflow: 'hidden',
        outline: 0,
        cursor: equals(disabled, true) ? 'not-allowed' : 'pointer',
        textDecoration: 'none',
        transition: 'opacity 0.2s ease-in-out, border 0.1s ease-in-out'
      }),
      inner: Object.assign({}, longhand('borderBottom', {
        style: 'dotted',
        color: fill,
        width: 1
      }), cssSides('padding', Side.A, 0), {
        lineHeight: 1.5,
        paddingBottom: 1
      })
    },
    hover: {
      root: {
        opacity: 0.75
      },
      inner: {
        borderBottomStyle: 'solid',
        opacity: 0.75
      }
    },
    active: {
      inner: {
        borderBottomStyle: 'solid',
        opacity: 0.75
      }
    },
    caps: {
      root: {
        textTransform: 'uppercase'
      }
    },
    block: {
      root: {
        display: 'block',
        width: percentage(100)
      }
    }
  }, _defineProperty(_reactCSS, modifier('size', Size.SM), {
    root: {
      fontSize: px2em(12)
    }
  }), _defineProperty(_reactCSS, modifier('size', Size.MD), {
    root: {
      fontSize: px2em(14)
    }
  }), _defineProperty(_reactCSS, modifier('size', Size.LG), {
    root: {
      fontSize: px2em(16)
    }
  }), _defineProperty(_reactCSS, modifier('size', Size.INHERIT), {
    root: {
      fontSize: 'inherit'
    }
  }), _defineProperty(_reactCSS, modifier('contentType', ContentType.PHONE_NUMBER), {
    inner: {
      borderBottomWidth: 0
    }
  }), _defineProperty(_reactCSS, "disabled", {
    root: {
      opacity: 0.3
    }
  }), _reactCSS), [{
    hover: hover
  }, {
    active: active
  }, {
    caps: caps
  }, {
    block: block
  }, {
    disabled: disabled
  }, {
    size: size
  }, {
    contentType: contentType
  }]);
};

var PureTextButton = function PureTextButton(_a) {
  var styles = _a.styles,
      children = _a.children,
      domProps = __rest(_a, ["styles", "children"]);

  return React.createElement(DynamicLink, Object.assign({
    style: styles.root
  }, domProps), React.createElement("span", {
    style: styles.inner
  }, children));
};
var enhance = pureEnhance('TextButton', {
  hocs: [hover, handleActive],
  hooks: [defaultProps({
    disabled: false,
    block: false,
    caps: false,
    size: Size.MD,
    contentType: ContentType.DEFAULT
  }), withStylesHook(stylesheet), mapProps(omit(['theme', 'against', 'block', 'size', 'contentType', 'caps']))]
});
var TextButton = enhance(PureTextButton); // eslint-disable-next-line no-underscore-dangle

export default TextButton;
export { Against, ContentType, Size };
